import * as React from 'react';
import styled from '@emotion/styled';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import { Box, Typography as MuiTypography } from '@mui/material';
import CardContent from '@mui/material/CardContent';
import { CustomButton } from '../CustomButton';
import { useTranslation } from 'next-i18next';
import { getText } from '../../utils/get-text';
import { useAppContext } from '../../contexts/AppContext';

export interface CardContentProps {
  title?: string | undefined;
  description?: string | undefined;
  limit?: number | undefined;
  className?: string;
  hideDescription?: boolean | undefined;
  hidePlayButton?: boolean | undefined;
}

const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

const CardContentStyled = styled(CardContent)(({ theme }) => ({
  width: '100%',
  paddingBottom: `${theme.spacing(1)} !important`,
  justifyContent: 'center',
  maxHeight: 85,
  [theme.breakpoints.up('sm')]: {
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    textAlign: 'left',
    padding: theme.spacing(1, 1.5),
    height: '100%',
    border: theme.theme_component_card_content_border || 'none',
    borderTop: 'none',
    pointerEvents: 'none',
  },
})) as typeof CardContent;

const CardTitle = styled(Typography)(({ theme }) => ({
  width: '100%',
  display: 'inline-block',
  color: '#000',
}));

const CardContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  '& span': {
    flex: 1,
    marginRight: theme.spacing(1),
  },
}));

const Description = styled(Typography)(({ theme }) => ({
  height: 'auto',
  minHeight: '45px',
  display: '-webkit-box',
  '-webkit-line-clamp': '2',
  overflow: 'hidden',
  'text-overflow': 'ellipsis',
  '-webkit-box-orient': 'vertical',
}));

const ButtonStyled = styled(CustomButton)(({ theme }) => ({
  display: 'flex',
  margin: 0,
  pointerEvents: 'auto',
  alignSelf: 'center',
  padding: theme.spacing(0.4, 4.5),
  borderRadius: theme.theme_component_carousel_a_card_button_border_radius || theme.spacing(9),
  background: theme.theme_component_carousel_a_card_button_background || theme.palette.primary.main,
  color: theme.theme_component_carousel_a_card_button_color || theme.palette.primary.contrastText,
  '&:hover': {
    background: theme.theme_component_carousel_a_card_button_hover_background_color,
    color: theme.theme_component_carousel_a_card_button_active_background_color
  },
  [theme.breakpoints.down(1300)]: {
    padding: '0px 4px',
  },
}));

// Todo: HUZ2ERA-1101 - Allow Typography props to be drilled (now we have them hardcoded in our jsx).
//       Had trouble extending our CardContentCProps...
export const CollectionContentA = (props: CardContentProps) => {
  const { description, hidePlayButton, title, limit, className, hideDescription } = props;
  const body2Ref = React.useRef<HTMLInputElement | null>(null);

  const { t } = useTranslation('card_content_c');
  const { useConfig } = useAppContext();
  const { componentOptions } = useConfig();
  

  return (
    <CardContentStyled className={className}>
      <CardContentWrapper>
        <CardTitle variant="caption" noWrap>
          {getText(title || '', limit || 35)}
        </CardTitle>
        {!hideDescription && (
          <Description ref={body2Ref} variant="body2" color="textPrimary">
            {description && getText(description, limit || 75)}
          </Description>
        )}
        {!hidePlayButton && (
          <ButtonStyled
            variant="contained"
            disableElevation
            disableRipple
            disableFocusRipple
            size="small"
            className="card-content-button"
          >
            {componentOptions.component_card_title_text || t('play_now', 'Play')}
          </ButtonStyled>
        )}
      </CardContentWrapper>
    </CardContentStyled>
  );
};
